/**
 * Utility functions for calculating ramp dimensions using calibration points
 */

import { CalibrationPoint, Ramp } from "../types";
import { getCanvasDimensionsFromRamp } from "./calibrationPointMigration";

/**
 * Minimal type for GPS calibration point data used in calculations
 * Does not require id or ramp_id fields
 */
export type GPSPointData = {
  x: number;
  y: number;
  latitude: number;
  longitude: number;
};

/**
 * Calculate the distance between two points using the Haversine formula
 * Returns distance in feet
 */
export const calculateDistanceBetweenPoints = (
  point1: GPSPointData,
  point2: GPSPointData
): number => {
  const R = 6371000; // Earth's radius in meters
  const lat1Rad = (point1.latitude * Math.PI) / 180;
  const lat2Rad = (point2.latitude * Math.PI) / 180;
  const deltaLatRad = ((point2.latitude - point1.latitude) * Math.PI) / 180;
  const deltaLngRad = ((point2.longitude - point1.longitude) * Math.PI) / 180;

  const a =
    Math.sin(deltaLatRad / 2) * Math.sin(deltaLatRad / 2) +
    Math.cos(lat1Rad) *
      Math.cos(lat2Rad) *
      Math.sin(deltaLngRad / 2) *
      Math.sin(deltaLngRad / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const distanceMeters = R * c;
  const distanceFeet = distanceMeters * 3.28084; // Convert meters to feet

  return distanceFeet;
};

/**
 * Calculate the scale factor from any pair of calibration points
 * Calibration points use relative coordinates (0-1), so we must convert to
 * pixel coordinates before calculating distance to account for aspect ratio.
 * Returns feet per pixel, or null if calculation is not possible
 */
export const calculateScaleFactor = (
  points: GPSPointData[],
  imageWidth: number,
  imageHeight: number
): number | null => {
  if (points.length < 2) return null;

  // Find the pair of points with the largest pixel distance for best accuracy
  let bestPair: { point1: GPSPointData; point2: GPSPointData } | null = null;
  let maxPixelDistance = 0;

  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const point1 = points[i];
      const point2 = points[j];

      // Convert relative coordinates (0-1) to pixel coordinates
      // This ensures x and y are in the same units
      const pixelDistance = Math.sqrt(
        Math.pow((point2.x - point1.x) * imageWidth, 2) +
          Math.pow((point2.y - point1.y) * imageHeight, 2)
      );

      if (pixelDistance > maxPixelDistance) {
        maxPixelDistance = pixelDistance;
        bestPair = { point1, point2 };
      }
    }
  }

  if (!bestPair || maxPixelDistance === 0) return null;

  // Calculate GPS distance between the best pair
  const gpsDistance = calculateDistanceBetweenPoints(
    bestPair.point1,
    bestPair.point2
  );

  // Calculate scale factor: feet per pixel
  const scaleFactor = gpsDistance / maxPixelDistance;

  return scaleFactor;
};

/**
 * Calculate ramp width using the scale factor and image width
 * Returns width in feet, or null if calculation is not possible
 */
export const calculateRampWidth = (
  points: GPSPointData[],
  imageWidth: number,
  imageHeight: number
): number | null => {
  const scaleFactor = calculateScaleFactor(points, imageWidth, imageHeight);
  if (!scaleFactor) return null;

  // Width = scaleFactor (feet/pixel) * imageWidth (pixels) = feet
  return scaleFactor * imageWidth;
};

/**
 * Calculate ramp depth using the scale factor and image height
 * Returns depth in feet, or null if calculation is not possible
 */
export const calculateRampDepth = (
  points: GPSPointData[],
  imageWidth: number,
  imageHeight: number
): number | null => {
  const scaleFactor = calculateScaleFactor(points, imageWidth, imageHeight);
  if (!scaleFactor) return null;

  // Depth = scaleFactor (feet/pixel) * imageHeight (pixels) = feet
  return scaleFactor * imageHeight;
};

/**
 * Get detailed calculation results including which points were used
 * Supports both synchronous (for backward compatibility) and asynchronous (with aspect ratio) modes
 */
export function getRampDimensionsFromCalibration(points: GPSPointData[]): {
  width: number | null;
  depth: number | null;
  message: string;
};

export function getRampDimensionsFromCalibration(
  points: GPSPointData[],
  ramp: Ramp
): Promise<{
  width: number | null;
  depth: number | null;
  message: string;
}>;

export function getRampDimensionsFromCalibration(
  points: GPSPointData[],
  ramp?: Ramp
): any {
  if (points.length < 2) {
    const result = {
      width: null,
      depth: null,
      message:
        "At least 2 calibration points are needed to calculate dimensions",
    };
    return ramp ? Promise.resolve(result) : result;
  }

  if (!ramp) {
    // Synchronous version - for backward compatibility
    // Use square aspect ratio (1000x1000) since we don't have image dimensions
    const width = calculateRampWidth(points, 1000, 1000);
    const depth = calculateRampDepth(points, 1000, 1000);

    return {
      width,
      depth,
      message: "",
    };
  } else {
    // Async version with ramp parameter
    return (async () => {
      // Default to square image if we can't load dimensions
      let imageWidth = 1000;
      let imageHeight = 1000;

      try {
        const canvasDimensions = await getCanvasDimensionsFromRamp(ramp);
        if (canvasDimensions) {
          imageWidth = canvasDimensions.width;
          imageHeight = canvasDimensions.height;
        }
      } catch (error) {
        console.warn(
          "Could not determine image dimensions, using 1000x1000:",
          error
        );
      }

      const width = calculateRampWidth(points, imageWidth, imageHeight);
      const depth = calculateRampDepth(points, imageWidth, imageHeight);

      return {
        width,
        depth,
        message: "",
      };
    })();
  }
}
